// Phase 3 interview timing — pure helpers used by CandidateInterviewPage (unit-tested).
//
// Alex (the ElevenLabs interviewer) has no clock. Everything it knows about time comes
// from silent [SYSTEM] contextual updates sent by the candidate page:
//   - a time check every 5 minutes (this file), until
//   - the existing T-5 "about 5 minutes remaining" message takes over.

export const TIME_CHECK_INTERVAL_SEC = 300;   // every 5 minutes
export const WRAP_UP_LEAD_SEC = 300;          // from T-5 the existing wrap-up messages take over
export const RESUME_THRESHOLD_SEC = 60;       // server says the interview began >60s ago → resuming

/**
 * Epoch ms (in THIS browser's clock) at which the interview clock starts.
 *  - First session: `connectMs`, the moment the call connected. The server's started_at is
 *    stamped before the browser has even connected (interview 56: ~33 s early), so using it
 *    would make every time check fire early.
 *  - Resuming (server says the interview began more than RESUME_THRESHOLD_SEC ago): the
 *    server's started_at/server_time pair, so elapsed time survives the reload and isn't
 *    skewed by a wrong browser clock.
 */
export function interviewStartMs(sessionData, receivedAtMs, connectMs) {
  const started = Date.parse(sessionData?.started_at || '');
  const server = Date.parse(sessionData?.server_time || '');
  if (Number.isNaN(started) || Number.isNaN(server) || !receivedAtMs) return connectMs;
  const serverElapsedMs = server - started;
  if (serverElapsedMs <= RESUME_THRESHOLD_SEC * 1000) return connectMs;
  return receivedAtMs - serverElapsedMs;
}

/** The 5-minute mark to announce now, or null. Only the latest mark (no burst after a reload). */
export function dueTimeCheck(elapsedSec, durationSec, lastMark) {
  if (elapsedSec >= durationSec - WRAP_UP_LEAD_SEC) return null;
  const mark = Math.floor(elapsedSec / TIME_CHECK_INTERVAL_SEC);
  return mark >= 1 && mark > lastMark ? mark : null;
}

/** Silent [SYSTEM] time check: elapsed, remaining, and a suggested time per remaining problem. */
export function timeCheckMessage(elapsedSec, durationSec, problemCount) {
  const elapsedMin = Math.floor(elapsedSec / 60);
  const totalMin = Math.round(durationSec / 60);
  const remainingMin = Math.max(0, totalMin - elapsedMin);
  const workMin = Math.max(0, remainingMin - WRAP_UP_LEAD_SEC / 60);   // last 5 minutes = wrap-up
  const problems = Math.max(1, problemCount || 1);
  const pace = [];
  for (let left = problems; left >= 1; left -= 1) {
    pace.push(`${left} → about ${Math.max(1, Math.floor(workMin / left))} min each`);
  }
  // Interview 60: Alex closed at 21 of 45 min, skipping the reserve problem and the honest-finish line,
  // although these checks arrived — so each check now repeats the finish rule at the decision point.
  return `[SYSTEM] Time check: ${elapsedMin} of ${totalMin} minutes elapsed, about ${remainingMin} minutes remaining. ` +
    `Pace guide (${problems} main problems) — if this many main problems are still unfinished, spend: ${pace.join('; ')}. ` +
    `FINISH RULE: only when all main problems AND the verification round are done — ` +
    (remainingMin > 10
      ? `more than 10 minutes remain, so ask the RESERVE PROBLEM next (if there is none, say "We've covered everything I had planned, so we're finishing a little early." then the closing). `
      : `10 minutes or less remain, so say "We've covered everything I had planned, so we're finishing a little early." then the closing. `) +
    `Never close the interview any other way before a [SYSTEM] message says about 5 minutes remain.`;
}
